'use client'

import { useState } from 'react'
import Link from 'next/link'
import type { BlogPost } from './blogData'
import VideoPill from './VideoPill'

// ── Row types ────────────────────────────────────────────────────────
// Every row is an article lesson. Posts that embed a video (post.hasVideo,
// derived in getAllBlogPosts) get the red Video pill instead of the emoji.

type TableRow = { post: BlogPost; group: string; displayTitle: string; learningOutcome: string; order: number }

// ── Desktop table for a numbered group ───────────────────────────────

function GroupTable({
  groupName,
  groupIndex,
  rows,
}: {
  groupName: string
  groupIndex: number
  rows: TableRow[]
}) {
  return (
    <div className="mb-8">
      <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
        <span className="text-blue-400 font-mono text-xs bg-blue-400/10 px-2 py-0.5 rounded">
          {groupIndex}
        </span>
        {groupName}
      </h3>
      <div className="border border-gray-800 rounded-xl overflow-hidden">
        <table className="w-full border-collapse">
          <tbody>
            {rows.map((row, i) => {
              const number = `${groupIndex}.${i + 1}`

              return (
                <tr key={row.post.id} className="border-b border-gray-800/50 last:border-b-0 hover:bg-gray-800/20 transition-colors">
                  {/* Number */}
                  <td className="py-3 pl-4 pr-2 align-top w-12 text-center">
                    <span className="text-xs font-mono text-gray-500">{number}</span>
                  </td>

                  {/* Type badge */}
                  <td className="py-3 pr-3 align-top w-8 text-center">
                    {row.post.hasVideo ? (
                      <VideoPill />
                    ) : (
                      <span className="text-blue-400/60 text-sm">
                        {row.post.category === 'course' ? '📋' : '📘'}
                      </span>
                    )}
                  </td>

                  {/* Title */}
                  <td className="py-3 pr-4 align-top">
                    <Link
                      href={`/blog/${row.post.id}`}
                      className="text-sm text-white hover:text-blue-400 transition-colors font-medium"
                    >
                      {row.displayTitle}
                      {isNew(row.post.publishedAt) && (
                        <span className="ml-2 text-[10px] font-semibold text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded">
                          NEW
                        </span>
                      )}
                    </Link>
                  </td>

                  {/* Learning outcome */}
                  <td className="hidden lg:table-cell py-3 pr-4 align-top text-sm text-gray-500 max-w-[280px]">
                    {row.learningOutcome}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Mobile group ─────────────────────────────────────────────────────

function MobileGroup({
  groupName,
  groupIndex,
  rows,
}: {
  groupName: string
  groupIndex: number
  rows: TableRow[]
}) {
  return (
    <div className="mb-6">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1 flex items-center gap-2">
        <span className="text-blue-400 font-mono bg-blue-400/10 px-1.5 py-0.5 rounded text-[10px]">
          {groupIndex}
        </span>
        {groupName}
      </h3>
      <div className="bg-[#111111] border border-gray-800 rounded-xl px-4">
        {rows.map((row, i) => (
          <MobileRow
            key={row.post.id}
            row={row}
            number={`${groupIndex}.${i + 1}`}
          />
        ))}
      </div>
    </div>
  )
}

// ── Mobile row ───────────────────────────────────────────────────────

function MobileRow({ row, number }: { row: TableRow; number: string }) {
  return (
    <Link
      href={`/blog/${row.post.id}`}
      className="block py-3 border-b border-gray-800 last:border-b-0"
    >
      <div className="flex items-start gap-2.5">
        <span className="text-[11px] font-mono text-gray-500 mt-0.5 w-7 flex-shrink-0">
          {number}
        </span>
        <div className="min-w-0">
          <p className="text-sm text-white font-medium leading-snug">
            {row.post.hasVideo && <VideoPill className="mr-2" />}
            {row.displayTitle}
            {isNew(row.post.publishedAt) && (
              <span className="ml-2 text-[10px] font-semibold text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded">
                NEW
              </span>
            )}
          </p>
          <p className="text-xs text-gray-500 mt-1 line-clamp-1">
            {row.learningOutcome}
          </p>
        </div>
      </div>
    </Link>
  )
}

// ── Named line icons (lucide) ────────────────────────────────────────

const lineIcons: Record<string, React.ReactNode> = {
  bot: (
    <>
      <rect width="18" height="10" x="3" y="11" rx="2" />
      <circle cx="12" cy="5" r="2" />
      <path d="M12 7v4" />
      <line x1="8" x2="8" y1="16" y2="16" />
      <line x1="16" x2="16" y1="16" y2="16" />
    </>
  ),
  zap: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />,
}

function SectionIcon({ icon }: { icon: string }) {
  if (icon.startsWith('/')) {
    return (
      <img
        src={icon}
        alt=""
        className="h-8 w-8 flex-shrink-0 rounded-lg object-cover"
      />
    )
  }

  const line = lineIcons[icon]
  return (
    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-400/10 text-blue-400">
      {line ? (
        <svg
          className="h-[18px] w-[18px]"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          viewBox="0 0 24 24"
        >
          {line}
        </svg>
      ) : (
        <span className="text-base">{icon}</span>
      )}
    </span>
  )
}

// ── Collapsible section (for "All" view) ─────────────────────────────

function CollapsibleSection({
  topicLabel,
  count,
  children,
  defaultOpen = false,
  icon,
  blurb,
}: {
  topicLabel: string
  count: number
  children: React.ReactNode
  defaultOpen?: boolean
  icon?: string
  blurb?: string
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="border border-gray-800 rounded-xl overflow-hidden mb-3">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-800/40 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          {icon && <SectionIcon icon={icon} />}
          <div className="min-w-0 text-left">
            <div className="flex items-center gap-2">
              <h2 className="text-white font-semibold text-sm">{topicLabel}</h2>
              <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full">
                {count} lessons
              </span>
            </div>
            {blurb && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">{blurb}</p>
            )}
          </div>
        </div>
        <svg
          className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div className={`px-5 pb-4 ${open ? '' : 'hidden'}`}>{children}</div>
    </div>
  )
}

// ── Standalone guide card (for non-curriculum items) ─────────────────

function StandaloneCard({
  title,
  href,
  isVideo,
}: {
  title: string
  href?: string
  isVideo?: boolean
}) {
  const inner = (
    <div className="flex items-center gap-2 px-3 py-2 bg-gray-800/40 border border-gray-700/50 rounded-lg hover:bg-gray-700/40 hover:border-gray-600 transition-colors">
      {isVideo && <VideoPill size="sm" />}
      <span className="text-xs text-gray-300 font-medium truncate">{title}</span>
    </div>
  )

  if (!href) return inner

  const isExternal = href.startsWith('http')
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {inner}
      </a>
    )
  }
  return <Link href={href}>{inner}</Link>
}

// ── Helpers ──────────────────────────────────────────────────────────

function isNew(dateStr: string): boolean {
  const diff = Date.now() - new Date(dateStr).getTime()
  return diff < 30 * 24 * 60 * 60 * 1000
}

// ── Exports ──────────────────────────────────────────────────────────

export {
  GroupTable,
  MobileGroup,
  MobileRow,
  CollapsibleSection,
  StandaloneCard,
  isNew,
}
export type { TableRow }
